import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { RowAction } from 'src/app/core/interfaces/row-action.interface';
import { PermissionService } from 'src/app/core/services/permission.service';

@Component({
  selector: 'app-table-row-actions-v1',
  templateUrl: './table-row-actions.component.html',
  styleUrl: './table-row-actions.component.scss'
})
export class TableRowActionsComponent implements OnInit, OnChanges {
  @Input({ required: true })
  set actions(value: RowAction[]) {
    this._actions = (value || []).map(action => this.normalizeAction(action));
    this.updateAllowActions();
  }
  get actions(): RowAction[] {
    return this._actions;
  }

  @Input({ required: true }) item: any;

  allowActions: RowAction[] = [];
  private _actions: RowAction[] = [];

  constructor(private perm: PermissionService) { }

  ngOnInit() {
    this.updateAllowActions();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['actions'] || changes['item']) {
      this.updateAllowActions();
    }
  }

  private normalizeAction(action: RowAction): RowAction {
    return {
      ...action,
      label: action.label || (action as any).lebel,
      disabled: typeof action.disabled === 'function'
        ? (action.disabled as any)()
        : (action.disabled ?? false),
      subActions: (action.subActions || []).map(sub => this.normalizeAction(sub))
    };
  }

  isImageIcon(icon?: string): boolean {
    if (!icon) return false;
    return icon.includes('.') || icon.includes('/') || icon.startsWith('data:image');
  }

  private updateAllowActions() {
    this.allowActions = (this.actions || [])
      .filter(action => {
        // Check permission
        const hasPerm = this.isAllowed(action.premission);
        if (!hasPerm) return false;

        // Check condition if exists
        if (action.condition && typeof action.condition === 'function') {
          return action.condition(this.item);
        }

        return true;
      });
  }

  trackByAction(index: number, action: RowAction): any {
    return action.icon || action.label || index;
  }

  onActionClick(action: RowAction, item: any, event: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.isDisabled(action)) return;

    if (action.callback) {
      action.callback(item);
    }
  }

  private isAllowed(permission?: string | string[]): boolean {
    if (!permission) return true;
    if (Array.isArray(permission)) {
      return permission.some(p => this.perm.hasPermission(p));
    }
    return this.perm.hasPermission(permission);
  }

  isDisabled(action: RowAction): boolean {
    return !!action.disabled;
  }

  isActionLoading(action: RowAction): boolean {
    if (typeof action.isLoading === 'function') {
      return action.isLoading(this.item);
    }
    return !!action.isLoading;
  }

  /** Use modern colors from toolbar-v1. */
  getActionBgColor(action: RowAction): string {
    if (action.backgroundColor) return action.backgroundColor;

    const icon = (action.icon || '').toLowerCase();
    const label = (action.label || '').toLowerCase();

    // Danger actions: trash, delete, remove
    if (icon.includes('trash') || icon.includes('delete') || icon.includes('remove') || label.includes('delete')) {
      return '#ef4444'; // Modern Red (danger-soft)
    }

    // Success actions: check, complete, approve, save
    if (icon.includes('check') || label.includes('complete') || label.includes('save')) {
      return '#10b981'; // Modern Green (success-soft)
    }

    // Default: Blue (primary)
    return '#3300FF';
  }

  getActionTooltip(action: RowAction): string {
    if (action.label) return action.label; // Return label if exists

    const icon = (action.icon || '').toLowerCase();

    if (icon.includes('eye')) return 'COMMON.VIEW';
    if (icon.includes('edit')) return 'COMMON.EDIT';
    if (icon.includes('trash')) return 'COMMON.DELETE';
    if (icon.includes('key')) return 'AUTHS.CHANGE_PASSWORD';
    if (icon.includes('print')) return 'COMMON.PRINT';
    if (icon.includes('file-pdf') || icon.includes('export')) return 'COMMON.EXTRACT';
    if (icon.includes('check-double')) return 'COMMON.POST';
    if (icon.includes('arrow-right')) return 'COMMON.TRANSFER';
    if (icon.includes('sync')) return 'COMMON.RETURN';
    if (icon.includes('balance-scale')) return 'COMMON.JOURNALS';
    if (icon.includes('plus')) return 'COMMON.ADD';

    return '';
  }
}
